import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  Wrap
} from '@chakra-ui/react';
import { FaBirthdayCake, FaCalendarAlt } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import PageLayout from './PageLayout';

const meses = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const formatDiaMes = (date) => {
  const dia = String(date.getDate()).padStart(2, '0');
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}`;
};

const parseDate = (item) => {
  const raw = item.data_nascimento || item.nascimento;
  if (!raw) return null;
  const value = String(raw);
  let day;
  let month;
  let year;

  if (value.includes('/')) {
    [day, month, year] = value.split('/');
  } else {
    const segment = value.split('T')[0];
    [year, month, day] = segment.split('-');
  }

  if (!year || !month || !day) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return new Date(Number(year), Number(month) - 1, Number(day));
};

const AniversarioPage = () => {
  const [membros, setMembros] = useState([]);
  const [familia, setFamilia] = useState([]);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE}/getMembros?id_pessoa=0&id_insituicao=0`)
      .then(response => setMembros(response.data.result[0]))
      .catch(error => console.error('Erro ao buscar membros:', error));

    axios.get(`${API_BASE}/getFamiliaPessoa?id_pessoa=0`)
      .then(response => setFamilia(response.data.result[0]))
      .catch(error => console.error('Erro ao buscar familia:', error));
  }, []);

  const handleHomeClick = () => {
    navigate('/');
  };

  const membrosNoMes = useMemo(() => (
    membros.filter((item) => {
      const date = parseDate(item);
      return date && date.getMonth() + 1 === mesSelecionado;
    })
  ), [membros, mesSelecionado]);

  const familiaNoMes = useMemo(() => (
    familia.filter((item) => {
      const date = parseDate(item);
      return date && date.getMonth() + 1 === mesSelecionado;
    })
  ), [familia, mesSelecionado]);

  const aniversariantesOrdenados = useMemo(() => {
    const map = {};

    const addItem = (item, origem) => {
      const date = parseDate(item);
      if (!date || date.getMonth() + 1 !== mesSelecionado) return;
      const key = formatDiaMes(date);
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push({ item, origem });
    };

    membros.forEach((item) => addItem(item, 'membro'));
    familia.forEach((item) => addItem(item, 'familia'));

    const keys = Object.keys(map).sort((a, b) => {
      const [diaA, mesA] = a.split('/').map(Number);
      const [diaB, mesB] = b.split('/').map(Number);
      return mesA === mesB ? diaA - diaB : mesA - mesB;
    });

    return keys.map((key) => ({ data: key, itens: map[key] }));
  }, [membros, familia, mesSelecionado]);

  const totalMes = membrosNoMes.length + familiaNoMes.length;

  const obterReferencia = (item) => {
    if (!item.id_familia) {
      return 'Membro';
    }
    const membroAssociado = membros.find(m => m.id_pessoa === item.id_pessoa);
    const base = item.tipo ? item.tipo : 'Familia';
    if (membroAssociado && membroAssociado.nome) {
      return `${base} do Irmão ${membroAssociado.nome}`;
    }
    return base;
  };

  return (
    <PageLayout
      title="Aniversários"
      subtitle="Calendário"
      onBack={handleHomeClick}
      accent="#93C8E6"
      background="#EEF3F7"
      watermark="/maconaria.png"
    >
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={5}>
        <Box bg="white" borderRadius="22px" p={5} boxShadow="0 12px 30px rgba(15,23,42,0.22)">
          <HStack spacing={3} mb={3}>
            <Box
              width="44px"
              height="44px"
              borderRadius="14px"
              bg="rgba(147,200,230,0.5)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="blue.700"
            >
              <Icon as={FaCalendarAlt} />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">Mes selecionado</Text>
              <Text fontWeight="bold" color="gray.800">{meses[mesSelecionado - 1]}</Text>
            </Box>
          </HStack>
          <Wrap spacing={2}>
            {meses.map((mes, index) => {
              const value = index + 1;
              const isSelected = value === mesSelecionado;
              return (
                <Box
                  key={mes}
                  as="button"
                  onClick={() => setMesSelecionado(value)}
                  px={3}
                  py={2}
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={isSelected ? 'blue.400' : 'gray.200'}
                  bg={isSelected ? 'blue.50' : 'white'}
                  color="gray.800"
                  boxShadow={isSelected ? '0 8px 18px rgba(15,23,42,0.12)' : 'none'}
                  _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
                  display="flex"
                  alignItems="center"
                  gap="6px"
                >
                  <Icon as={FaCalendarAlt} color={isSelected ? 'blue.600' : 'gray.500'} />
                  <Text fontWeight={isSelected ? 'bold' : 'medium'}>{mes.slice(0, 3)}</Text>
                </Box>
              );
            })}
          </Wrap>
        </Box>

        <Box bg="white" borderRadius="22px" p={5} boxShadow="0 12px 30px rgba(15,23,42,0.22)">
          <Text fontSize="sm" color="gray.500">Total no mes</Text>
          <Text fontSize="3xl" fontWeight="bold" color="gray.800">{totalMes}</Text>
          <HStack spacing={3} mt={3}>
            <Badge colorScheme="blue" variant="subtle">Membros {membrosNoMes.length}</Badge>
            <Badge colorScheme="purple" variant="subtle">Familia {familiaNoMes.length}</Badge>
          </HStack>
        </Box>
      </SimpleGrid>

      <Stack spacing={5}>
        {aniversariantesOrdenados.length === 0 && (
          <Box bg="white" borderRadius="22px" p={8} boxShadow="0 12px 30px rgba(15,23,42,0.22)">
            <Text color="gray.500">Sem aniversariantes neste mes.</Text>
          </Box>
        )}

        {aniversariantesOrdenados.map((grupo) => (
          <Box
            key={grupo.data}
            bg="white"
            borderRadius="22px"
            p={5}
            boxShadow="0 12px 30px rgba(15,23,42,0.22)"
          >
            <Flex align="center" justify="space-between" mb={4}>
              <HStack spacing={3}>
                <Box
                  bg="rgba(147,200,230,0.5)"
                  borderRadius="14px"
                  px={3}
                  py={2}
                >
                  <Text fontWeight="bold" color="gray.800">{grupo.data}</Text>
                </Box>
                <Text color="gray.500" fontSize="sm">{meses[mesSelecionado - 1]}</Text>
              </HStack>
              <Badge colorScheme="blue" variant="subtle">{grupo.itens.length} pessoas</Badge>
            </Flex>
            <Stack spacing={3}>
              {grupo.itens.map(({ item, origem }) => (
                <Flex
                  key={item.id_pessoa || item.id_familia}
                  align="center"
                  justify="space-between"
                  bg="gray.50"
                  borderRadius="16px"
                  p={3}
                >
                  <HStack spacing={3}>
                    <Box
                      width="38px"
                      height="38px"
                      borderRadius="12px"
                      bg="rgba(147,200,230,0.4)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="blue.700"
                    >
                      <FaBirthdayCake />
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.800">{item.nome}</Text>
                      <Text fontSize="sm" color="gray.500">{obterReferencia(item)}</Text>
                    </Box>
                  </HStack>
                  <Badge colorScheme={origem === 'familia' ? 'purple' : 'blue'} variant="subtle">
                    {origem === 'familia' ? 'Familia' : 'Membro'}
                  </Badge>
                </Flex>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </PageLayout>
  );
};

export default AniversarioPage;
